import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  changePassword,
  getMe,
  login,
  logout,
  type TokenResponse,
} from "@/lib/api/auth";
import { useAuthStore } from "@/stores/auth";

export function useLogin() {
  const setSession = useAuthStore((s) => s.setSession);
  return useMutation({
    mutationFn: login,
    onSuccess: (data: TokenResponse) => {
      setSession({
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        expiresAt: data.expires_at,
        user: data.user,
        permissions: data.permissions,
      });
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  const refreshToken = useAuthStore((s) => s.refreshToken);
  const clear = useAuthStore((s) => s.clear);

  return useMutation({
    mutationFn: async () => {
      if (refreshToken) {
        try {
          await logout(refreshToken);
        } catch {
          /* ignore — server clears on its own once tokens expire */
        }
      }
    },
    onSettled: () => {
      clear();
      queryClient.clear();
    },
  });
}

export function useMe(enabled: boolean) {
  const setUser = useAuthStore((s) => s.setUser);
  return useQuery({
    queryKey: ["auth", "me"],
    queryFn: async () => {
      const data = await getMe();
      setUser(data.user, data.permissions);
      return data;
    },
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useChangePassword() {
  return useMutation({ mutationFn: changePassword });
}
